/**
 * "Track style…" (1.9.0): the track's default cell, colour, background and size (the same controls as a cue's), for
 * the asset track (subtitle_tracks.style) and the sequence track (subtitles.style). Named TrackStyleEditor so it does
 * not shadow the contracts type TrackStyle.
 */
import { DEFAULT_TRACK_STYLE, type CueStylePatch, type TrackStyle } from '@prism/contracts';
import { Popover } from './Popover';
import { AlignCells } from './PlacementGrid';
import { StyleFields } from './CueStylePopover';

export interface TrackStyleEditorProps {
  value: TrackStyle;
  /** The new track style; null: back to the default look. */
  onChange(style: TrackStyle | null): void;
  readOnly?: boolean;
  testId?: string;
}

/** A track style with a patch applied (null removes a field); null when nothing is left. */
export function patchTrackStyle(style: TrackStyle, patch: CueStylePatch): TrackStyle | null {
  const next: Record<string, unknown> = { ...style };
  for (const [k, v] of Object.entries(patch)) { if (v === null || v === undefined) delete next[k]; else next[k] = v; }
  return Object.keys(next).length ? (next as TrackStyle) : null;
}

export function TrackStyleEditor({ value, onChange, readOnly, testId = 'track-style' }: TrackStyleEditorProps) {
  const custom = Object.keys(value).length > 0;
  return (
    <Popover label="Track style" title="The track’s default placement, colour, background and size" disabled={readOnly} testId={testId}
      className={`btn ghost sm ${custom ? 'on' : ''}`} button={<>Track style…</>} width={260}>
      {() => (
        <>
          <p className="faint" style={{ margin: '0 0 6px', fontSize: 12 }}>Cues without their own style use these.</p>
          <div className="sub-style-row">
            <span className="sub-style-label">Placement</span>
            <AlignCells value={value.align ?? DEFAULT_TRACK_STYLE.align} testId={`${testId}-grid`} onPick={(a) => onChange(patchTrackStyle(value, { align: a === DEFAULT_TRACK_STYLE.align ? null : a }))} />
          </div>
          <StyleFields value={value} fallback={DEFAULT_TRACK_STYLE} testId={testId} onChange={(p) => onChange(patchTrackStyle(value, p))} />
          <button type="button" className="btn ghost sm" style={{ width: '100%', marginTop: 4 }} disabled={!custom} data-testid={`${testId}-reset`} onClick={() => onChange(null)}>Default look</button>
        </>
      )}
    </Popover>
  );
}
